import { useState, useEffect, useRef, Suspense } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  Outlet
} from 'react-router-dom';

// I18n
import './translations';

// Redux
import { Provider, useDispatch } from 'react-redux';
import { store } from '@/redux/store';
import { setUserInfo } from '@/redux/slices/userInfo';

// Connections
import { getInfoUserApi } from '@/connections';

import { NotificationProvider } from '@/components/Notification';

// Components
import { MobileScreen } from '@/components';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Sentry } from '@/sentry';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Externals
import { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';

// Helpers
import { logOutActions } from '@/helpers';
import {
  SESSION_ALERT_REASONS,
  getCurrentSessionId,
  getRefreshFailureReason,
  requestTokenRefresh,
  withRefreshLock
} from '@/helpers/authSession';
import { lazyWithRetry } from '@/helpers/lazyWithRetry';
import { LogoSmallAnimated } from '@/assets/icons';

// Lazy Loaded Pages
const Home = lazyWithRetry(() => import('./Pages/Home/'));
const TemplateEditor = lazyWithRetry(() => import('./Pages/Editor/TemplateEditor'));
const TemplateEditor2 = lazyWithRetry(() => import('./Pages/Editor/TemplateEditor2'));
const Register2 = lazyWithRetry(() => import('./Pages/Register/Register2'));
const Login2 = lazyWithRetry(() => import('./Pages/Login/Login2'));
const AutoLogin = lazyWithRetry(() => import('./Pages/Login/AutoLogin'));
const NotFound = lazyWithRetry(() => import('./Pages/Not-Found/NotFound'));
const GoodRegister = lazyWithRetry(() => import('./Pages/Register/GoodRegister'));
const ThanksRegister = lazyWithRetry(() => import('./Pages/Register/ThanksRegister'));
const PasswordRest = lazyWithRetry(() => import('./Pages/ForgetPassword/PasswordRest'));
const PassDisplay = lazyWithRetry(() => import('./Pages/ForgetPassword/PassDisplay'));
const PasswordNew = lazyWithRetry(() => import('./Pages/ForgetPassword/PasswordNew'));
const SharedSign = lazyWithRetry(() => import('./Pages/SharedSign'));
const Manual = lazyWithRetry(() => import('./Pages/Manual/Manual'));
const ProfileSection = lazyWithRetry(() => import('./Pages/Profile/ProfileSection'));
const ManualVerification = lazyWithRetry(() => import('./Pages/ManualVerification/ManualVerification'));
const DocumentValidator = lazyWithRetry(() => import('./Pages/DocumentValidator/DocumentValidator'));
const DemoTerranza = lazyWithRetry(() => import('./Pages/DemoTerranza'));
const Template1 = lazyWithRetry(() => import('./components/Templates/Template1'));

// Lazy load from specific files
const Sign = lazyWithRetry(() => import('./Pages/Sign'));
const TemplateFill = lazyWithRetry(() => import('./Pages/TemplateFill'));
const Redirect = lazyWithRetry(() => import('./Pages/Redirect'));
const VerifyPayment = lazyWithRetry(() => import('./Pages/VerifyPayment'));
const Plans = lazyWithRetry(() => import('./Pages/Plans'));

// Tope de renovaciones silenciosas consecutivas de la sesión de cuenta
// mientras el usuario lee un documento compartido en /shared (ver
// TokenVerification). Acota cuánto puede vivir una sesión sin ninguna
// confirmación humana, aun con una pestaña abierta indefinidamente.
const MAX_SILENT_REFRESHES = 4;

// /auth/refresh respondió 403. Con `code: SESSION_REPLACED` la cookie ya no es
// la sesión vigente (la cuenta entró en otro dispositivo): no se llama al
// logout del backend para no tocar la sesión vigente. Con REFRESH_EXPIRED la
// sesión simplemente venció y el logout normal limpia las cookies.
const handleRefreshRejected = async (err, navigate) => {
  const reason = getRefreshFailureReason(err);
  sessionStorage.setItem('sessionAlert', reason);
  if (reason === SESSION_ALERT_REASONS.EXPIRED) {
    await logOutActions(navigate);
  } else {
    await logOutActions(navigate, { preserveGlobalSession: true, skipServerLogout: true });
  }
};

function App () {
  const [language, setLanguage] = useState('es');
  const [isMobile, setIsMobile] = useState(false);
  const GOOGLE_CLIENT_ID =
    '431348673926-2njrk45po1774e6v0iqobdce8tprf6s7.apps.googleusercontent.com';

  const handleLanguageChange = (selectedLanguage) => {
    setLanguage(selectedLanguage);
  };

  const PrivateRoutes = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    // 'authenticated' | 'unauthenticated' | 'checking': mientras esté en 'checking'
    // NO se renderiza <Outlet/> — así ninguna ruta privada se expone con datos
    // de localStorage sin confirmar primero contra el backend (ver auth/profile).
    const [status, setStatus] = useState(() =>
      sessionStorage.getItem('isAuth') ? 'authenticated' : 'checking'
    );

    useEffect(() => {
      if (status !== 'checking') return;

      const tokenExp = localStorage.getItem('tokenExp');
      const userId = localStorage.getItem('userId');
      const looksValid = tokenExp && userId && Number(tokenExp) > Date.now() / 1000;

      if (!looksValid) {
        setStatus('unauthenticated');
        return;
      }

      // El indicio de localStorage nunca basta por sí solo para dar acceso:
      // se confirma con el backend (cookie/JWT real) antes de mostrar cualquier
      // ruta privada. Si el backend lo rechaza, se limpia el indicio local.
      let cancelled = false;
      getInfoUserApi()
        .then((info) => {
          if (cancelled) return;
          if (!info || !info.id) {
            setStatus('unauthenticated');
            return;
          }
          dispatch(setUserInfo(info));
          localStorage.setItem('userInfo', JSON.stringify(info));
          sessionStorage.setItem('isAuth', 'true');
          // Para que Sentry pueda contar usuarios distintos afectados por un
          // error (hoy "Users" siempre marca 0 porque nunca se llamaba) y,
          // si hace falta investigar un caso puntual, poder ubicar la sesión.
          Sentry.setUser({ id: info.id, email: info.email });
          setStatus('authenticated');
        })
        .catch((error) => {
          if (cancelled) return;
          // 401 aquí significa sesión inválida/expirada (esperado). Cualquier otro
          // código (500, red caída, etc.) es un fallo real del backend y sí debe alertar.
          if (error?.response?.status !== 401) {
            Sentry.captureException(error, { tags: { source: 'App-PrivateRoutes-sessionCheck' } });
          }
          localStorage.removeItem('tokenExp');
          localStorage.removeItem('userId');
          setStatus('unauthenticated');
        });

      return () => { cancelled = true; };
    }, [status, dispatch]);

    if (status === 'checking') return <Loader />;

    return <> {status === 'authenticated' ? <Outlet /> : <Navigate to="/login" state={{ from: location }} replace />}</>;
  };

  const PublicRoutes = () => {
    const auth = sessionStorage.getItem('isAuth');

    return <> {!auth ? <Outlet /> : <Navigate to="/home" />}</>;
  };

  useEffect(() => {
    // Listener para detectar cambios en localStorage (provenientes de otras pestañas)
    const handleStorageChange = (event) => {
      if (event.key === 'globalSessionId') {
        const newGlobalSessionId = event.newValue;
        const isAuth = sessionStorage.getItem('isAuth');

        // Si esta pestaña ESTÁ autenticada, y el ID global cambió o se borró desde otra pestaña
        if (isAuth === 'true') {
          if (newGlobalSessionId) {
            // Otra pestaña inició una nueva sesión, invalidando la nuestra
            sessionStorage.setItem('sessionAlert', 'true');
          }
          // Si newGlobalSessionId es null, es un logout normal de otra pestaña.
          // En ambos casos, forzamos logout en esta pestaña también, sin
          // llamar al backend: las cookies son compartidas y ya pertenecen a
          // la sesión nueva (o ya las limpió la otra pestaña).
          logOutActions(null, { skipServerLogout: true });
        }
      }
    };

    // La cuenta inició sesión en otro dispositivo (evento de socket). Se espera
    // a que termine cualquier refresh en curso y se compara con el sessionId
    // de este navegador: si coincide, la sesión nueva es la nuestra (otra
    // pestaña acaba de iniciar sesión o renovar) y no se hace nada.
    const handleSessionReplaced = async (event) => {
      if (sessionStorage.getItem('isAuth') !== 'true') return;
      const replacedBy = event.detail?.sessionId || null;
      await withRefreshLock(async () => {});
      if (sessionStorage.getItem('isAuth') !== 'true') return;
      if (replacedBy && replacedBy === getCurrentSessionId()) return;

      sessionStorage.setItem('sessionAlert', SESSION_ALERT_REASONS.OTHER_DEVICE);
      await logOutActions(null, { preserveGlobalSession: true, skipServerLogout: true });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('signly:session-replaced', handleSessionReplaced);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('signly:session-replaced', handleSessionReplaced);
    };
  }, []);

  // Componente interno para manejar la redirección y verificación del token
  function TokenVerification() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const originalTitle = document.title;
    const timerRef = useRef(null);
    // Cuenta refrescos silenciosos consecutivos en /shared sin que el usuario
    // haga nada. La acotamos (ver MAX_SILENT_REFRESHES) para no convertir una
    // pestaña abierta y desatendida en una sesión de cuenta que se renueva
    // sola indefinidamente sin ninguna señal de presencia humana.
    const silentRefreshCountRef = useRef(0);

    // Limpiar tokenInvite proactivamente si el usuario sale de las rutas /shared
    useEffect(() => {
      if (!location.pathname.startsWith('/shared')) {
        sessionStorage.removeItem('tokenInvite');
        silentRefreshCountRef.current = 0;
      }
    }, [location.pathname]);

    // Rutas públicas que no requieren verificación
    const publicRoutes = [
      '/register',
      '/plans',
      '/login',
      '/verifyRegister',
      '/resetPassword',
      '/verifyPass',
      '/newPass',
      '/thanksRegister',
      '/terranza',
      '/shared/',
      '/verify-payment',
      '/manual'
    ];

    useEffect(() => {
      const checkToken = async () => {
        // Si no está autenticado, no se hace nada.
        if (!sessionStorage.getItem('isAuth')) {
          return;
        }

        const path = location.pathname;
        const isPublicRoute = publicRoutes.some(publicRoute =>
          path.startsWith(publicRoute)
        );

        // Un usuario CON CUENTA que entró a /shared desde "Compartidos conmigo"
        // (isRegularAccess=true) sigue teniendo su sesión de cuenta corriendo
        // de fondo. No queremos el modal interactivo interrumpiendo su lectura,
        // pero tampoco queremos que esa sesión expire en silencio sin refrescarse
        // mientras lee por 1h+ — así que a él SÍ lo seguimos evaluando, en modo
        // silencioso. Un invitado real nunca llega aquí (no tiene isAuth).
        const isSilentAccountGuestView = path.startsWith('/shared') &&
          localStorage.getItem('isRegularAccess') === 'true';

        // Si estamos en ruta pública (y no es el caso anterior), no se ejecuta la verificación.
        if (isPublicRoute && !isSilentAccountGuestView) {
          return;
        }

        // Obtenemos la fecha de expiración del token desde localStorage.
        const tokenExpStr = localStorage.getItem('tokenExp');
        if (!tokenExpStr) {
          // Si no se tiene el valor, forzamos logout (salvo en modo silencioso:
          // no interrumpimos la lectura del documento compartido por esto).
          if (!isSilentAccountGuestView) {
            await logOutActions(navigate);
          }
          return;
        }

        const tokenExp = Number(tokenExpStr);
        const currentTime = Date.now() / 1000;
        let timeLeft = tokenExp - currentTime;

        // Si el token tiene más de 5 minutos (300 seg) de validez...
        if (timeLeft > 300) {
          document.title = originalTitle;
          // Programamos la próxima verificación para cuando queden 5 minutos.
          timerRef.current = setTimeout(checkToken, (timeLeft - 300) * 1000);
        } else if (isSilentAccountGuestView) {
          // Quedan 5 min o menos (o ya expiró): refrescamos en segundo plano,
          // sin modal ni cambios de título, para no interrumpir la lectura.
          //
          // Tope de seguridad: sin este límite, una pestaña abierta y
          // desatendida en /shared podría renovar la sesión de cuenta para
          // siempre sin que nadie confirme que sigue ahí (no hay ningún otro
          // control de inactividad en el frontend). Acotamos a
          // MAX_SILENT_REFRESHES renovaciones silenciosas consecutivas
          // (~55 min c/u); pasado ese punto dejamos de auto-renovar y la
          // sesión de cuenta expira de forma normal si nadie interactúa.
          if (silentRefreshCountRef.current >= MAX_SILENT_REFRESHES) {
            return;
          }
          try {
            const refreshData = await requestTokenRefresh();
            if (refreshData.exp) {
              silentRefreshCountRef.current += 1;
              if (silentRefreshCountRef.current < MAX_SILENT_REFRESHES) {
                const newTimeLeft = Number(refreshData.exp) - Date.now() / 1000;
                if (newTimeLeft > 300) {
                  timerRef.current = setTimeout(checkToken, (newTimeLeft - 300) * 1000);
                } else if (newTimeLeft > 0) {
                  timerRef.current = setTimeout(checkToken, 30 * 1000);
                }
              }
            }
            // Si no viene `exp`, no forzamos nada: se resolverá (logout) cuando
            // el usuario salga de /shared y checkToken corra en modo normal.
          } catch {
            // Refresh silencioso falló (refreshToken vencido/rotado por otra
            // sesión). No interrumpimos la lectura; el logout real ocurrirá al
            // salir de /shared.
          }
        } else if (timeLeft > 0) {
          // Si quedan 5 minutos o menos: mostramos alerta para extender la sesión.
          // Actualizamos el título cada segundo para mostrar la cuenta regresiva.
          const timerInterval = setInterval(() => {
            timeLeft = Math.floor(tokenExp - Date.now() / 1000);
            document.title = `⚠️ Sesión expira en ${timeLeft} seg`;
          }, 1000);

          const result = await Swal.fire({
            title: 'Sesión a punto de expirar',
            html: `Tu sesión expirará en <strong id="swal-timer">${Math.floor(timeLeft)}</strong> segundos.<br>¿Desea extender su sesión?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Continuar sesión',
            cancelButtonText: 'Cerrar sesión',
            timer: timeLeft * 1000,
            timerProgressBar: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              const swalTimerInterval = setInterval(() => {
                const content = Swal.getHtmlContainer();
                if (content) {
                  const b = content.querySelector('#swal-timer');
                  if (b) {
                    b.textContent = Math.floor(Swal.getTimerLeft() / 1000);
                  }
                }
              }, 1000);
              Swal.getPopup().dataset.timerInterval = swalTimerInterval;
            },
            didClose: () => {
              const popup = Swal.getPopup();
              if (popup && popup.dataset.timerInterval) {
                clearInterval(popup.dataset.timerInterval);
              }
            }
          });

          clearInterval(timerInterval);
          document.title = originalTitle;

          if (result.isConfirmed) {
            try {
              // Renovamos la sesión (o reutilizamos la que otra pestaña ya renovó).
              const refreshData = await requestTokenRefresh();
              if (refreshData.exp) {
                const newTimeLeft = Number(refreshData.exp) - Date.now() / 1000;
                if (newTimeLeft > 300) {
                  timerRef.current = setTimeout(checkToken, (newTimeLeft - 300) * 1000);
                } else if (newTimeLeft > 0) {
                  checkToken();
                }
              } else {
                await logOutActions(navigate);
              }
            } catch (err) {
              if (err?.response?.status === 403) {
                await handleRefreshRejected(err, navigate);
              } else {
                // Falla real (red caída, backend caído, etc.) — no confundir con
                // "otra sesión te ganó", y sí dejar rastro en Sentry.
                Sentry.captureException(err, { tags: { source: 'App-checkToken-refresh' } });
                await logOutActions(navigate);
              }
            }
          } else {
            // Si el modal se cerró por tiempo pero otra pestaña de este
            // navegador ya extendió la sesión, no cerramos: solo reprogramamos.
            // "Cerrar sesión" (cancel) siempre cierra.
            const latestTimeLeft = Number(localStorage.getItem('tokenExp')) - Date.now() / 1000;
            if (result.dismiss !== Swal.DismissReason.cancel && latestTimeLeft > 300) {
              timerRef.current = setTimeout(checkToken, (latestTimeLeft - 300) * 1000);
            } else {
              await logOutActions(navigate);
            }
          }
        } else {
          // El access token ya expiró (ej. el usuario volvió de leer un
          // documento compartido por más de 1h). Antes de forzar logout,
          // intentamos un refresh silencioso: el refreshToken vive 24h y no
          // depende de que el access token siga vigente, así que la sesión
          // real puede seguir siendo válida. Solo si el refresh también
          // falla, cerramos sesión.
          try {
            const refreshData = await requestTokenRefresh();
            if (refreshData.exp) {
              const newTimeLeft = Number(refreshData.exp) - Date.now() / 1000;
              if (newTimeLeft > 300) {
                timerRef.current = setTimeout(checkToken, (newTimeLeft - 300) * 1000);
              } else if (newTimeLeft > 0) {
                checkToken();
              }
            } else {
              await logOutActions(navigate);
            }
          } catch (err) {
            if (err?.response?.status === 403) {
              await handleRefreshRejected(err, navigate);
            } else {
              Sentry.captureException(err, { tags: { source: 'App-checkToken-silentRefreshOnReturn' } });
              await logOutActions(navigate);
            }
          }
        }
      };

      checkToken();

      return () => {
        // Limpiamos cualquier timeout pendiente cuando el componente se desmonte.
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, [navigate, location.pathname, searchParams, originalTitle]);

    return null;
  }

  const Loader = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#F9FAFB',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <LogoSmallAnimated color="#285FF6" />
    </div>
  );

  const isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'dev';
  const shouldRenderEditors = false; // Set to true to enable editors in dev environment

  if (isMobile) {
    return <MobileScreen />;
  }

  return (
    <Provider store={store}>
      <Toaster
        position="bottom-right"
        toastOptions={{
          success: {
            style: {
              background: '#285FF6',
              color: '#fff'
            }
          },
          error: {
            style: {
              background: '#ff2d2d',
              color: '#fff'
            }
          }
        }}
      />
      <NotificationProvider>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <BrowserRouter>
          <TokenVerification />
            <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route index element={<Login2 />} />
                <Route path="/validator" element={<DocumentValidator />} />
                <Route path="/manualDeUsuario" element={<Manual />} />
                <Route element={<PublicRoutes />}>
                  <Route path="/register" element={<Register2 />} />
                  <Route path="/login" element={<Login2 />} />
                  <Route path="/autologin" element={<AutoLogin />} />
                  <Route path="/verifyRegister" element={<GoodRegister />} />
                  <Route path="/resetPassword" element={<PasswordRest />} />
                  <Route path="/verifyPass" element={<PassDisplay />} />
                  <Route path="/newPass" element={<PasswordNew />} />
                  <Route
                    path="/thanksRegister"
                    element={<ThanksRegister language={language} />}
                  />
                  <Route path="/terranza" element={<Template1 />} />
                </Route>
                <Route path="/shared" element={<SharedSign />} />
                <Route element={<PrivateRoutes />}>
                  <Route path="/demoterranza" element={<DemoTerranza />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/settings" element={<ProfileSection />} />
                  <Route path="/sign" element={<Sign />} />
                  <Route path="/plantilla/:id" element={<TemplateFill />} />
                  {shouldRenderEditors && isDev && (
                    <>
                      <Route path="/editorDEV" element={<TemplateEditor2 />} />
                      <Route path="/editor" element={<TemplateEditor />} />
                    </>
                  )}
                  <Route path="/redirect" element={<Redirect />} />
                  {/* <Route path="/manual_verification" element={<ManualVerification />} /> */}

                </Route>
                <Route path="/plans" element={<Plans />} />
                <Route path="/verify-payment" element={<VerifyPayment />} />
                {/* Ruta 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </NotificationProvider>
    </Provider>
  );
}

export default App;
